import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { notify, Stack } from '@cast/design-system';
import {
  invalidateClusterDashboardQueries,
  useClusterDashboardState,
} from '@cast/features-cluster-shared';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { LazyLoadedBox } from '@cast/shared-layout-ui';
import { EksControlPlaneCostsAlert } from '@cast/features-cluster-ui';

import { AutoOptimizationProvider } from '@cast/one-click-features-opsPilot-optimizationWizard';

import { OptimizeBanner } from './_components/OptimizeBanner';
import { PerformanceBanner } from './_components/PerformanceBanner';
import { ClusterDashboardHeader } from './_components/ClusterDashboardHeader';
import { ClusterMetadataBar } from './_components/ClusterMetadataBar';
import { MonthlyClusterCostSection } from './_components/MonthlyClusterCostSection';
import { OomKillsSection } from './_components/OomKillsSection';
import { ResourceUtilizationSection } from './_components/ResourceUtilizationSection';
import { ResourcesSection } from './_components/ResourcesSection';
import { ServiceHealthSection } from './_components/ServiceHealthSection';
import { useClusterDashboardBanners } from './_hooks/useClusterDashboardBanners';
import { ExploreAutomationUpsellBanner } from 'features/cluster/_components/ExploreAutomationUpsellBanner';

const LAZY_SECTION_ROOT_MARGIN = '200px 0px';

export const ClusterDashboard = () => {
  const queryClient = useQueryClient();
  const { cluster } = useCluster();
  const { hasOldK8s, isInaccessible } = useClusterDashboardState();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { activeBanner } = useClusterDashboardBanners();

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await invalidateClusterDashboardQueries(queryClient, cluster.id);
    } catch {
      notify.error('Failed to refresh dashboard');
    } finally {
      setIsRefreshing(false);
    }
  }, [cluster.id, queryClient]);

  if (isInaccessible) {
    return null;
  }

  return (
    <Stack gap={24}>
      <ClusterDashboardHeader
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />
      {hasOldK8s ? <EksControlPlaneCostsAlert /> : null}
      <ExploreAutomationUpsellBanner variant="dashboard" />
      <ClusterMetadataBar />
      {activeBanner === 'performance' ? <PerformanceBanner /> : null}
      {activeBanner === 'optimize' ? (
        <AutoOptimizationProvider>
          <OptimizeBanner />
        </AutoOptimizationProvider>
      ) : null}
      <ResourcesSection />
      <LazyLoadedBox
        data-testid="service-health-lazy-section"
        rootMargin={LAZY_SECTION_ROOT_MARGIN}
      >
        <ServiceHealthSection />
      </LazyLoadedBox>
      <LazyLoadedBox
        data-testid="resource-utilization-lazy-section"
        rootMargin={LAZY_SECTION_ROOT_MARGIN}
      >
        <ResourceUtilizationSection />
      </LazyLoadedBox>
      <LazyLoadedBox
        data-testid="monthly-cost-lazy-section"
        rootMargin={LAZY_SECTION_ROOT_MARGIN}
      >
        <MonthlyClusterCostSection />
      </LazyLoadedBox>
      <LazyLoadedBox
        data-testid="oom-kills-lazy-section"
        rootMargin={LAZY_SECTION_ROOT_MARGIN}
      >
        <OomKillsSection />
      </LazyLoadedBox>
    </Stack>
  );
};
